import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Maybe } from '@/shared/libs';
import {
  Button,
  Dialog,
  DialogFooter,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  LoaderOverlay,
} from '@/shared/ui';
import type { Deal } from '@psp-aggregator/sdk';

import { useRevokeDealMutation } from '../libs/useDealMutations';
import { useDeals } from '../libs/useDeals';

import { DEAL_STATUS_I18N_KEY } from '../model/status';

interface DealDetailsProps {
  deal: Deal;
  onClose: () => void;
}

const DealDetails: FC<DealDetailsProps> = ({ deal, onClose }) => {
  const { t } = useTranslation();
  const revoke = useRevokeDealMutation(deal.id);
  const canRevoke = deal.status === 'draft' || deal.status === 'in_review';

  return (
    <Dialog
      title={deal.merchant_brand || deal.id}
      width={640}
      onClose={onClose}>
      <div className="max-h-[min(60dvh,calc(90dvh-10rem))] space-y-4 overflow-y-auto overscroll-contain">
        <div className="grid gap-3 rounded-xl bg-muted p-4 sm:grid-cols-3">
          <div>
            <p className="text-xs text-muted-foreground">
              {t('pages.deals.fields.status')}
            </p>
            <p className="font-medium">
              {t(DEAL_STATUS_I18N_KEY[deal.status])}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">
              {t('pages.deals.fields.country')}
            </p>
            <p className="font-medium">{deal.merchant_country}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">
              {t('pages.deals.fields.currency')}
            </p>
            <p className="font-medium">{deal.merchant_currency}</p>
          </div>
        </div>
        <div className="space-y-2">
          {deal.items.map((item) => (
            <div key={item.id} className="rounded-xl border border-border p-3">
              <p className="font-medium">{item.offer_alias}</p>
              <p className="text-xs text-muted-foreground">
                {t('pages.deals.items.rates', {
                  count: [
                    item.offer.pay_in,
                    item.offer.pay_out,
                    item.offer.settlement,
                    item.offer.crypto_top_up,
                  ].filter(Boolean).length,
                })}
              </p>
            </div>
          ))}
        </div>
      </div>
      <DialogFooter
        endSlot={
          <>
            {canRevoke && (
              <Button
                disabled={revoke.isPending}
                type="button"
                variant="secondary"
                onClick={() => revoke.mutate({})}>
                {t('pages.deals.actions.revoke')}
              </Button>
            )}
            <Button type="button" onClick={onClose}>
              {t('buttons.close')}
            </Button>
          </>
        }
      />
    </Dialog>
  );
};

DealDetails.displayName = 'DealDetails';

export interface DealListPanelProps {
  onOpenDeal?: (dealId: string) => void;
  source?: 'agent_outbound' | 'merchant_inbound';
}

export const DealListPanel: FC<DealListPanelProps> = ({
  onOpenDeal,
  source,
}) => {
  const { t } = useTranslation();
  const deals = useDeals({ source });
  const [selectedDeal, setSelectedDeal] = useState<Maybe<Deal>>(null);

  if (deals.isLoading) return <LoaderOverlay show />;
  if (deals.isError)
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{t('errors.deals.loadFailed')}</EmptyTitle>
          <EmptyDescription>{t('errors.unknown')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  if (!deals.data?.items.length)
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{t('pages.deals.empty.title')}</EmptyTitle>
          <EmptyDescription>
            {t('pages.deals.empty.description')}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );

  return (
    <>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {deals.data.items.map((deal) => (
          <button
            key={deal.id}
            className="cursor-pointer rounded-2xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/30"
            type="button"
            onClick={() => {
              if (onOpenDeal) {
                onOpenDeal(deal.id);

                return;
              }
              setSelectedDeal(deal);
            }}>
            <div className="flex items-start justify-between gap-3">
              <p className="min-w-0 truncate font-semibold">
                {deal.merchant_brand || deal.id}
              </p>
              <span className="shrink-0 rounded-full bg-muted px-2 py-1 text-xs text-muted-foreground">
                {t(DEAL_STATUS_I18N_KEY[deal.status])}
              </span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              {deal.merchant_country} · {deal.merchant_currency}
            </p>
            <p className="mt-3 text-xs text-muted-foreground">
              {t('pages.deals.items.count', { count: deal.items.length })}
            </p>
          </button>
        ))}
      </div>
      {!!selectedDeal && (
        <DealDetails
          deal={selectedDeal}
          onClose={() => setSelectedDeal(null)}
        />
      )}
    </>
  );
};

DealListPanel.displayName = 'DealListPanel';
